import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid-pro';
import { GmailAuditModel, GmailExportKey } from '../../../../types';
import { useUserSettings } from '../../../utils/use-user-settings';
import {
  ToolsRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import AvatarChip from '../../../shared/avatar-chip';
import { useMemo, useState } from 'react';
import GmailExportToolbar from './gmail-export-toolbar';
import UpdateKeyDialog from './update-key-dialog';
import CreateGmailExportDialog from './create-gmail-export-dialog';
import dayjs from 'dayjs';
import DeleteGmailExportsDialog from './delete-gmail-exports-dialog';
import { createGridOverlays } from '../../grid-overlay/no-data-overlay-helpers';
import DownloadGmailExportDialog from './download-gmail-export-dialog';
import SharedDataGridClient from '../../../shared/shared-data-grid-client';
import { useToolbar } from '../../../shared/shared-data-grid-toolbar-helper';

import Badge from '@mui/material/Badge';
import Tooltip from '@mui/material/Tooltip';
import AttachEmail from '@mui/icons-material/AttachEmail';
import Download from '@mui/icons-material/Download';

interface GmailExportGridProps {
  isLoading: boolean;
  isFetching: boolean;
  toolsRoles: string[];
  changeLogRoles: string[];
  gmailAuditModels: GmailAuditModel[] | undefined;
  gmailExportKey: GmailExportKey | undefined;
  apiUnavailable?: boolean;
}

export default function GmailExportGrid({
  isLoading,
  isFetching,
  toolsRoles,
  changeLogRoles,
  gmailAuditModels,
  gmailExportKey,
  apiUnavailable,
}: GmailExportGridProps) {
  const [openDeleteGmailExports, setOpenDeleteGmailExports] = useState(false);
  const [openAddGmailExport, setOpenAddGmailExport] = useState(false);
  const [openUpdateKey, setOpenUpdateKey] = useState(false);
  const [selectedExports, setSelectedExports] = useState<string[]>([]);
  const [selectedGmailAuditModel, setSelectedGmailAuditModel] =
    useState<GmailAuditModel | null>(null);
  const [openDownloadGmailExport, setOpenDownloadGmailExport] = useState(false);

  const columns: GridColDef<GmailAuditModel>[] = useMemo(
    () => [
      {
        field: 'requestDate',
        headerName: 'Request Date',
        pinnable: false,
        width: 150,
        type: 'date',
        valueGetter: (_params, row) => {
          return new Date(row.requestDate);
        },
        valueFormatter: (params: Date) => {
          const formattedDate = dayjs(params).format('M/D/YY, h:mm A');
          return formattedDate;
        },
      },
      {
        field: 'user',
        headerName: 'User',
        flex: 1,
        renderCell: (params) => {
          return <AvatarChip email={params.value} type="User" />;
        },
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 140,
        valueGetter: (value: GmailAuditModel['status']) => {
          if (value === 'COMPLETED') {
            return 'Export Complete';
          } else if (value === 'ERROR') {
            return 'Error in export';
          } else if (value === 'MARKED_DELETE') {
            return 'Delete pending';
          } else if (value === 'DELETED') {
            return 'Deleted';
          } else if (value === 'PENDING') {
            return 'Export Pending';
          } else {
            return 'Export expired after 3 weeks';
          }
        },
      },
      {
        field: 'searchQuery',
        headerName: 'Search Query',
        flex: 1,
        valueGetter: (value: string, row) => {
          if (row.includeDeleted) {
            return 'Entire mailbox with deleted messages';
          } else if (value.length > 0) {
            return value;
          } else {
            return 'Entire mailbox';
          }
        },
      },
      {
        field: 'headerOnly',
        headerName: 'Message Type',
        flex: 1,
        type: 'singleSelect',
        valueOptions: [
          { label: 'Full Messages', value: false },
          { label: 'Message Headers Only', value: true },
        ],
        valueGetter: (params: boolean) => {
          return params;
        },
      },
      {
        field: 'beginDate',
        headerName: 'Start Date',
        width: 135,
        type: 'date',
        valueGetter: (_params, row) => {
          return new Date(row.beginDate);
        },
        valueFormatter: (params: Date) => {
          if (params.getTime() === 0) {
            return 'N/A';
          } else {
            const formattedDate = dayjs(params).format('M/D/YY, h:mm A');
            return formattedDate;
          }
        },
      },
      {
        field: 'endDate',
        headerName: 'End Date',
        width: 140,
        type: 'date',
        valueGetter: (_params, row) => {
          return new Date(row.endDate);
        },
        valueFormatter: (params: Date) => {
          if (params.getTime() === 0) {
            return 'N/A';
          } else {
            const formattedDate = dayjs(params).format('M/D/YY, h:mm A');
            return formattedDate;
          }
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        getActions: (params) => {
          const buttonArray = [];
          buttonArray.push(
            <Tooltip
              arrow
              title={
                !isAbleToSeeUIElement(
                  toolsRoles,
                  ToolsRoles.DownloadGmailExport.value
                )
                  ? `You are missing the ${ToolsRoles.DownloadGmailExport.name} permission to perform this action.`
                  : params.row.status === 'PENDING'
                  ? 'The export request is still being processed.'
                  : params.row.status === 'ERROR'
                  ? 'The export request failed.'
                  : params.row.status === 'DELETED'
                  ? 'The export request has been deleted.'
                  : params.row.status === 'EXPIRED'
                  ? 'The mailbox files were deleted by Google after the 3 week retention limit.'
                  : params.row.downloadUrls.length === 0
                  ? 'There are no exports to download.'
                  : 'Configure Download'
              }
            >
              <span>
                <Badge
                  badgeContent={params.row.downloadUrls.length}
                  color="primary"
                  max={999}
                >
                  <GridActionsCellItem
                    label="Download"
                    icon={<Download />}
                    disabled={
                      !isAbleToSeeUIElement(
                        toolsRoles,
                        ToolsRoles.DownloadGmailExport.value
                      ) ||
                      params.row.status != 'COMPLETED' ||
                      params.row.downloadUrls.length < 1
                    }
                    onClick={() => {
                      setSelectedGmailAuditModel(params.row);
                      setOpenDownloadGmailExport(true);
                    }}
                  />
                </Badge>
              </span>
            </Tooltip>
          );
          return buttonArray;
        },
      },
    ],
    [toolsRoles, setSelectedGmailAuditModel, setOpenDownloadGmailExport]
  );

  const { viewType } = useUserSettings();
  const defaultGridState = {
    sorting: {
      sortModel: [{ field: 'requestDate', sort: 'desc' }],
    },
  };

  const toolbarProps = useMemo(
    () => ({
      gmailExportKey,
      toolsRoles,
      setOpenAddGmailExport,
      setOpenUpdateKey,
      gmailExportList: gmailAuditModels || [],
      selectedExports,
      setOpenDeleteGmailExports,
      apiUnavailable,
    }),
    [
      gmailAuditModels,
      gmailExportKey,
      selectedExports,
      toolsRoles,
      apiUnavailable,
    ]
  );

  return (
    <>
      <SharedDataGridClient
        slots={{
          toolbar: useToolbar(GmailExportToolbar, toolbarProps),
          ...createGridOverlays(
            apiUnavailable
              ? 'Gmail Export is No Longer Available'
              : 'Gmail Exports',
            <AttachEmail />,
            undefined,
            undefined,
            !!apiUnavailable
          ),
        }}
        rows={gmailAuditModels || []}
        columns={columns}
        defaultGridState={defaultGridState}
        gridKey={viewType.gmailExportsGridStates?.activeGridState}
        loading={isLoading || isFetching}
        checkboxSelection
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
        selectedRows={selectedExports}
        setSelectedRows={setSelectedExports}
        gridStateKey="gmailExportsGridStates"
      />
      {openAddGmailExport ? (
        <CreateGmailExportDialog
          open={openAddGmailExport}
          handleClose={() => setOpenAddGmailExport(false)}
          changeLogRoles={changeLogRoles}
        />
      ) : null}
      {openDeleteGmailExports && selectedExports.length > 0 ? (
        <DeleteGmailExportsDialog
          open={openDeleteGmailExports}
          handleClose={() => setOpenDeleteGmailExports(false)}
          changeLogRoles={changeLogRoles}
          modelsList={gmailAuditModels || []}
          selectedExports={selectedExports}
          setSelectedExports={setSelectedExports}
        />
      ) : null}
      {openUpdateKey ? (
        <UpdateKeyDialog
          open={openUpdateKey}
          handleClose={() => setOpenUpdateKey(false)}
          changeLogRoles={changeLogRoles}
          gmailExportKey={gmailExportKey}
          toolsRoles={toolsRoles}
        />
      ) : null}

      {openDownloadGmailExport && selectedGmailAuditModel ? (
        <DownloadGmailExportDialog
          open={openDownloadGmailExport}
          handleClose={() => setOpenDownloadGmailExport(false)}
          gmailAuditModel={selectedGmailAuditModel}
        />
      ) : null}
    </>
  );
}
